import React, { useState, useRef, useEffect } from 'react';
import styles from './codeValidation.module.scss';

interface CodeValidationProps {
  length?: number;
  onComplete?: (code: string) => void;
  onChange?: (code: string) => void;
  onBack?: () => void;
  onResend?: () => void;
  error?: string;
  label?: string;
  title?: string;
  subtitle?: React.ReactNode;
  isOpen?: boolean;
  inline?: boolean;
}

const CodeValidation: React.FC<CodeValidationProps> = ({
  length = 6,
  onComplete,
  onChange,
  onBack,
  onResend,
  error,
  label = 'Enter code',
  title = 'Verify Your Phone',
  subtitle,
  isOpen = false,
  inline = false,
}) => {
  const [digits, setDigits] = useState<string[]>(Array(length).fill(''));
  const inputsRef = useRef<(HTMLInputElement | null)[]>([]);

  useEffect(() => {
    if (isOpen) {
      const timer = requestAnimationFrame(() => {
        setDigits(Array(length).fill(''));
        inputsRef.current[0]?.focus();
      });
      return () => cancelAnimationFrame(timer);
    }
  }, [isOpen, length]);

  const handleChange = (index: number, value: string) => {
    const digit = value.replace(/\D/g, '').slice(-1);

    const newDigits = [...digits];
    newDigits[index] = digit;
    setDigits(newDigits);

    const code = newDigits.join('');
    onChange?.(code);

    if (digit && index < length - 1) {
      inputsRef.current[index + 1]?.focus();
    }

    if (digit && newDigits.every((d) => d !== '')) {
      onComplete?.(code);
    }
  };

  const handleKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace') {
      if (!digits[index] && index > 0) {
        const newDigits = [...digits];
        newDigits[index - 1] = '';
        setDigits(newDigits);
        inputsRef.current[index - 1]?.focus();
        onChange?.(newDigits.join(''));
      } else {
        const newDigits = [...digits];
        newDigits[index] = '';
        setDigits(newDigits);
        onChange?.(newDigits.join(''));
      }
    } else if (e.key === 'ArrowLeft' && index > 0) {
      inputsRef.current[index - 1]?.focus();
    } else if (e.key === 'ArrowRight' && index < length - 1) {
      inputsRef.current[index + 1]?.focus();
    }
  };

  const handlePaste = (e: React.ClipboardEvent) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, length);

    if (pastedData) {
      const newDigits = [...digits];
      for (let i = 0; i < pastedData.length; i++) {
        newDigits[i] = pastedData[i];
      }
      setDigits(newDigits);

      const code = newDigits.join('');
      onChange?.(code);

      const nextEmpty = newDigits.findIndex((d) => d === '');
      inputsRef.current[nextEmpty !== -1 ? nextEmpty : length - 1]?.focus();

      if (newDigits.every((d) => d !== '')) {
        onComplete?.(code);
      }
    }
  };

  if (!isOpen) return null;

  return (
    <div className={inline ? styles.inlineContainer : styles.overlay}>
      <div className={styles.overlayContent}>
        {onBack && (
          <button type="button" className={styles.backBtn} onClick={onBack}>
            <svg
              viewBox="0 0 24 24"
              width="20"
              height="20"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <polyline points="15 18 9 12 15 6"></polyline>
            </svg>
            Back
          </button>
        )}

        <h3 className={styles.title}>{title}</h3>
        {subtitle && <p className={styles.subtitle}>{subtitle}</p>}

        <div className={styles.codeValidation}>
          {label && <label className={styles.label}>{label}</label>}
          <div className={styles.inputContainer}>
            {digits.map((digit, index) => (
              <input
                key={index}
                ref={(el) => {
                  inputsRef.current[index] = el;
                }}
                type="text"
                inputMode="numeric"
                maxLength={1}
                value={digit}
                onChange={(e) => handleChange(index, e.target.value)}
                onKeyDown={(e) => handleKeyDown(index, e)}
                onPaste={index === 0 ? handlePaste : undefined}
                className={`${styles.digitInput} ${error ? styles.hasError : ''} ${digit ? styles.filled : ''}`}
                autoComplete="one-time-code"
              />
            ))}
          </div>
          {error && <span className={styles.errorMsg}>{error}</span>}
        </div>

        {onResend && (
          <button
            className={styles.resendBtn}
            onClick={() => {
              setDigits(Array(length).fill(''));
              requestAnimationFrame(() => inputsRef.current[0]?.focus());
              onResend();
            }}
          >
            Did not receive a code? <span>Resend</span>
          </button>
        )}
      </div>
    </div>
  );
};

export default CodeValidation;
